import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FormInstance } from 'antd/es/form';
import type { DefaultOptionType } from 'antd/es/select';
import type { KeyboardEvent } from 'react';

const LOCAL_TAG_ID_PREFIX = 'local:';

type NamedRecord = {
  id: string;
  name: string;
};

type UseCreatableTagFieldParams<T extends NamedRecord> = {
  form: FormInstance;
  fieldName: string;
  records: T[];
  create: (name: string) => Promise<T>;
  toOptions: (records: T[]) => DefaultOptionType[];
  getKnownIds?: (records: T[]) => string[];
  toLocalRecord?: (name: string, localId: string) => T;
};

function defaultKnownIds<T extends NamedRecord>(records: T[]): string[] {
  return records.map((record) => record.id);
}

function defaultLocalRecord<T extends NamedRecord>(
  name: string,
  localId: string,
): T {
  return { id: localId, name } as T;
}

function isLocalTagId(id: string): boolean {
  return id.startsWith(LOCAL_TAG_ID_PREFIX);
}

function nestedSelectOptions(node: DefaultOptionType): DefaultOptionType[] {
  const nested: unknown = node.options;
  if (!Array.isArray(nested)) {
    return [];
  }
  return nested.filter(
    (item): item is DefaultOptionType =>
      typeof item === 'object' && item !== null,
  );
}

function collectOptionLabelToId(
  options: DefaultOptionType[],
): Map<string, string> {
  const labelToId = new Map<string, string>();

  const visit = (nodes: DefaultOptionType[]) => {
    for (const node of nodes) {
      visit(nestedSelectOptions(node));
      if (node.value != null && typeof node.label === 'string') {
        labelToId.set(node.label, String(node.value));
      }
    }
  };

  visit(options);
  return labelToId;
}

export function uniqueIds(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    result.push(value);
  }
  return result;
}

function resolveExistingId(
  raw: string,
  knownIds: Set<string>,
  labelToId: Map<string, string>,
): string | undefined {
  if (knownIds.has(raw)) {
    return raw;
  }
  const exact = labelToId.get(raw);
  if (exact) {
    return exact;
  }
  const lower = raw.toLowerCase();
  for (const [label, id] of labelToId) {
    if (label.toLowerCase() === lower) {
      return id;
    }
  }
  return undefined;
}

export function useCreatableTagField<T extends NamedRecord>({
  form,
  fieldName,
  records,
  create,
  toOptions,
  getKnownIds = defaultKnownIds,
  toLocalRecord = defaultLocalRecord,
}: UseCreatableTagFieldParams<T>) {
  const [createdRecords, setCreatedRecords] = useState<T[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const createdRecordsRef = useRef(createdRecords);
  useEffect(() => {
    createdRecordsRef.current = createdRecords;
  }, [createdRecords]);

  const onSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const allRecords = useMemo(() => {
    const existingIds = new Set(getKnownIds(records));
    const extras = createdRecords.filter(
      (record) => !existingIds.has(record.id),
    );
    return extras.length ? [...records, ...extras] : records;
  }, [createdRecords, getKnownIds, records]);

  const knownIds = useMemo(
    () => new Set(getKnownIds(allRecords)),
    [allRecords, getKnownIds],
  );

  const options = useMemo(() => toOptions(allRecords), [allRecords, toOptions]);

  const labelToId = useMemo(() => collectOptionLabelToId(options), [options]);

  const selectedIds = useCallback((): string[] => {
    const current: unknown = form.getFieldValue(fieldName);
    return Array.isArray(current) ? uniqueIds(current.map(String)) : [];
  }, [fieldName, form]);

  const onChange = useCallback(
    (value: string[]) => {
      const resolved: string[] = [];
      const seen = new Set<string>();
      for (const item of value) {
        const id = resolveExistingId(item, knownIds, labelToId) ?? item;
        if (seen.has(id)) {
          continue;
        }
        seen.add(id);
        resolved.push(id);
      }
      form.setFieldsValue({ [fieldName]: resolved });
    },
    [fieldName, form, knownIds, labelToId],
  );

  const onInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return;
      }
      const raw = event.currentTarget.value.trim();
      if (!raw) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.value = '';
      setSearchValue('');

      const existingId = resolveExistingId(raw, knownIds, labelToId);
      const current = selectedIds();
      if (existingId) {
        if (!current.includes(existingId)) {
          form.setFieldsValue({
            [fieldName]: [...current, existingId],
          });
        }
        return;
      }

      const localId = `${LOCAL_TAG_ID_PREFIX}${crypto.randomUUID()}`;
      const localRecord = toLocalRecord(raw, localId);
      setCreatedRecords((prev) =>
        prev.some((record) => record.id === localId)
          ? prev
          : [...prev, localRecord],
      );
      form.setFieldsValue({
        [fieldName]: [...current, localId],
      });
    },
    [fieldName, form, knownIds, labelToId, selectedIds, toLocalRecord],
  );

  const commit = useCallback(
    async (ids: string[]): Promise<string[]> => {
      setPendingCount((count) => count + 1);
      try {
        const resolved: string[] = [];
        const replacements = new Map<string, T>();
        for (const id of uniqueIds(ids)) {
          if (!isLocalTagId(id)) {
            resolved.push(id);
            continue;
          }
          const local = createdRecordsRef.current.find(
            (record) => record.id === id,
          );
          if (!local) {
            continue;
          }
          const created = await create(local.name);
          replacements.set(id, created);
          resolved.push(created.id);
        }
        if (replacements.size > 0) {
          setCreatedRecords((prev) =>
            prev.map((record) => replacements.get(record.id) ?? record),
          );
        }
        const uniqueResolved = uniqueIds(resolved);
        form.setFieldsValue({ [fieldName]: uniqueResolved });
        return uniqueResolved;
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [create, fieldName, form],
  );

  return {
    options,
    onChange,
    onInputKeyDown,
    onSearch,
    searchValue,
    commit,
    isCreating: pendingCount > 0,
  };
}
